<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <label
        :id="id"
        class="el-checkbox qa-checkbox"
        :class="{
            'is-disabled': isDisabled,
            'is-checked': isChecked,
            'is-multiline': multilineLabel,
            [`multiline-limit--${multilineLabelLimit}`]: multilineLabel && multilineLabelLimit
        }"
        role="checkbox"
        tabindex="-1"
        :title="computedTitle"
        :aria-checked="isChecked"
        :aria-disabled="isDisabled"
    >
        <span class="el-checkbox__content qa-checkbox-content">
            <span
                class="el-checkbox__control qa-checkbox-control"
                :class="{ 'is-focused': focused}"
            >
                <a-glyph v-show="isChecked" name="check-square-16" class="el-checkbox__glyph" />
                <input
                    ref="input"
                    class="el-checkbox__original qa-checkbox-original"
                    type="checkbox"
                    :tabindex="isDisabled ? -1 : 0"
                    :name="name"
                    :value="label"
                    :true-value="trueLabel"
                    :false-value="falseLabel"
                    :disabled="isDisabled"
                    :checked="isChecked"
                    @keyup.enter="handleKeyUp"
                    @focus="handleFocus"
                    @blur="handleBlur"
                    @change="handleChange"
                />
            </span>
            <span
                v-if="label || $slots.default"
                class="el-checkbox__label qa-checkbox-label"
            >
                <slot><a-dangerous-html :content="label" /></slot>
            </span>
        </span>
        <span
            v-if="$slots.description"
            class="el-checkbox__description"
        >
            <slot name="description" />
        </span>
    </label>
</template>

<script setup>
import { computed, inject, watch, onMounted, ref } from 'vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import AGlyph from '@core/components/glyph/glyph.vue';

const focused = ref(false);
const emit = defineEmits(['update:modelValue', 'change']);
const props = defineProps({
    id: { type: String, default: '' },
    label: { type: [String, Number], default: '' },
    name: { type: String, default: '' },
    trueLabel: { type: [String, Number],default: undefined },
    falseLabel: { type: [String, Number], default: undefined },
    disabled: { type: Boolean, default: false },
    modelValue: { type: [Boolean, Array, String, Number], default: false },
    checked: Boolean,
    multilineLabel: { type: Boolean, default: false },
    multilineLabelLimit: {
        type: Number,
        validator: v => [2, 3].includes(v),
        default: 2,
    },
    showHoverHint: { type: Boolean, default: false },
});

const input = ref(null);
const checkboxGroup = inject('checkboxGroup', null);
const elFormItem = inject('elFormItem', null);

const selfModel = ref(false);
const isGroup = computed(() => Boolean(checkboxGroup));
const isDisabled = computed(() => props.disabled || checkboxGroup?.disabled?.value);

const model = computed({
    get() {
        if (isGroup.value) return checkboxGroup.modelValue.value;
        return props.modelValue !== undefined ? props.modelValue : selfModel.value;
    },
    set(val) {
        if (isGroup.value) checkboxGroup.updateModelValue(val);
        else if (props.modelValue !== undefined) emit('update:modelValue', val);
        else selfModel.value = val;
    },
});

const isChecked = computed(() => {
    const val = model.value;
    if (typeof val === 'boolean') return val;
    if (Array.isArray(val)) return val.includes(props.label);
    return val === props.trueLabel;
});

function handleChange(event) {
    const checked = event.target.checked;
    let newValue;

    if (Array.isArray(model.value)) {
        const copy = [...model.value];
        const exists = copy.includes(props.label);
        if (checked && !exists) copy.push(props.label);
        else if (!checked && exists) copy.splice(copy.indexOf(props.label), 1);

        if (checkboxGroup?.min !== undefined && copy.length < checkboxGroup.min) return;
        if (checkboxGroup?.max !== undefined && copy.length > checkboxGroup.max) return;

        newValue = copy;
    } else {
        newValue = checked
            ? (props.trueLabel ?? true)
            : (props.falseLabel ?? false);
    }

    model.value = newValue;
    emit('change', newValue, event);
    checkboxGroup?.emitChange?.(model.value);
    elFormItem?.markAsTouched?.();
    elFormItem?.validate?.('change');
    elFormItem?.validate?.('blur');
}

function handleFocus() {
    focused.value = true;
}

function handleBlur() {
    focused.value = false;
    elFormItem?.validate?.('blur');
};

const computedTitle = computed(() => props.showHoverHint ? String(props.label || '') : null);

function handleKeyUp() { input.value?.click(); }

onMounted(() => {
    if (props.checked && Array.isArray(model.value) && !model.value.includes(props.label)) {
        model.value = [...model.value, props.label];
    }
    if (props.id) {
        input.value?.closest('label')?.setAttribute('aria-controls', props.id);
    }
});

watch(() => props.checked, val => model.value = val);
watch(() => props.modelValue, () => {
    elFormItem?.markAsTouched?.();
    elFormItem?.validate?.('change');
});
</script>

<style lang="postcss" scoped>
.el-checkbox {
    @mixin body;
    display: block;
    text-align: start;
    color: var(--av-fixed-secondary);
    max-width: 100%;
    position: relative;
    cursor: pointer;
    outline: none;
    user-select: none;
    &__content {
        display: flex;
        gap: 8px;
        align-items: start;
    }
    &__control {
        display: flex;
        margin: 4px 0 0;
        box-sizing: border-box;
        align-items: center;
        justify-content: center;
        min-width: 16px;
        width: 16px;
        height: 16px;
        border: 1px solid var(--av-brand-light);
        border-radius: 2px;
        background-color: var(--av-inversed-primary);

        &.is-focused {
            box-shadow: 0 0 0 3px var(--av-fixed-focus);
        }
    }
    &.is-checked {
        .el-checkbox__control {
            color: var(--av-inversed-primary);
            border-color: var(--av-brand-light);
            background-color: var(--av-brand-primary);
        }
        .el-checkbox__glyph {
            fill: var(--av-inversed-primary);
            min-width: 16px;
            min-height: 16px;
        }
    }
    &.is-disabled {
        color: var(--av-fixed-lighter);
        cursor: default;
        .el-checkbox__control {
            color: var(--av-brand-light);
            background-color: var(--av-brand-accent);
            background-clip: border-box;
            border-color: var(--av-brand-accent);
        }
        .el-checkbox__glyph {
            fill: var(--av-brand-light);
        }
    }
    &.is-multiline {
        .el-checkbox__label {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }
    &.multiline-limit--1 {
        .el-checkbox__label {
            line-clamp: 1;
            -webkit-line-clamp: 1;
        }
    }
    &.multiline-limit--2 {
        .el-checkbox__label {
            line-clamp: 2;
            -webkit-line-clamp: 2;
        }
    }
    &.multiline-limit--3 {
        .el-checkbox__label {
            line-clamp: 3;
            -webkit-line-clamp: 3;
        }
    }
}
</style>
